import type { ChecklistItem, Goal, SubTask, Task, TimelineDate } from '../types/timeline'
import { isValidDate } from './safeDate'

/**
 * Single shared rule set for everything that writes a Goal / Task / Subtask:
 * AI Generate, AI Edit review, the Manage screen save. All three must reject
 * the same invalid shapes so we never persist nonsense.
 *
 * Issues are returned as a flat list keyed by the local node id, so the review
 * screen can correlate a tree row to the rules it broke.
 */

export type ValidationIssueCode =
  | 'TITLE_EMPTY'
  | 'TITLE_PUNCTUATION'
  | 'TITLE_REPETITIVE'
  | 'DATE_START_MISSING'
  | 'DATE_END_MISSING'
  | 'DATE_RANGE_INVERTED'
  | 'DATE_OUT_OF_BOUNDS'
  | 'DATE_OUTSIDE_PARENT'
  | 'CHECKLIST_TEXT_EMPTY'

export interface ValidationIssue {
  /** Local id of the node (Goal/Task/Subtask/Checklist) that has the issue. */
  nodeId: string
  /** Which kind of node carries the id — drives where the row is rendered. */
  nodeKind: 'goal' | 'task' | 'subtask' | 'checklist'
  /** Which field, if applicable. Drives the yellow border on the form. */
  field?: 'title' | 'startDate' | 'endDate' | 'text'
  code: ValidationIssueCode
  message: string
}

const MIN_REASONABLE_YEAR = 2000
const MAX_REASONABLE_YEAR = 2100

const isPunctuationOnly = (value: string): boolean => /^[\s\p{P}\p{S}]*$/u.test(value)

const isRepetitive = (value: string): boolean => {
  const tokens = value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  if (tokens.length < 3) return false
  return tokens.every((token) => token === tokens[0])
}

const dateOutOfBounds = (value: TimelineDate): boolean => {
  if (!isValidDate(value)) return true
  const year = new Date(value).getUTCFullYear()
  return year < MIN_REASONABLE_YEAR || year > MAX_REASONABLE_YEAR
}

const dateRangeInverted = (start: TimelineDate, end: TimelineDate): boolean => {
  if (!isValidDate(start) || !isValidDate(end)) return false
  return new Date(start).getTime() > new Date(end).getTime()
}

const dateInsideParent = (
  start: TimelineDate,
  end: TimelineDate,
  parentStart: TimelineDate,
  parentEnd: TimelineDate,
): boolean => {
  if (![start, end, parentStart, parentEnd].every(isValidDate)) return true
  const childStart = new Date(start).getTime()
  const childEnd = new Date(end).getTime()
  const ancestorStart = new Date(parentStart).getTime()
  const ancestorEnd = new Date(parentEnd).getTime()
  return childStart >= ancestorStart && childEnd <= ancestorEnd
}

const validateTitle = (
  value: string,
  nodeId: string,
  nodeKind: ValidationIssue['nodeKind'],
  issues: ValidationIssue[],
): void => {
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    issues.push({ nodeId, nodeKind, field: 'title', code: 'TITLE_EMPTY', message: 'Title is empty.' })
    return
  }
  if (isPunctuationOnly(trimmed)) {
    issues.push({
      nodeId,
      nodeKind,
      field: 'title',
      code: 'TITLE_PUNCTUATION',
      message: 'Title is only punctuation or symbols.',
    })
    return
  }
  if (isRepetitive(trimmed)) {
    issues.push({
      nodeId,
      nodeKind,
      field: 'title',
      code: 'TITLE_REPETITIVE',
      message: 'Title looks repetitive (same word three or more times).',
    })
  }
}

const validateDates = (
  start: TimelineDate,
  end: TimelineDate,
  nodeId: string,
  nodeKind: ValidationIssue['nodeKind'],
  issues: ValidationIssue[],
  /** Pass true on AI-edit / AI-generate; we tolerate missing dates in
   * "user pre-fill" flows (legacy items with no dates yet that the user is
   * about to fix). Defaults to strict. */
  requireDates: boolean = true,
): void => {
  if (!isValidDate(start)) {
    if (requireDates) {
      issues.push({
        nodeId,
        nodeKind,
        field: 'startDate',
        code: 'DATE_START_MISSING',
        message: 'Start date is missing or invalid.',
      })
    }
  } else if (dateOutOfBounds(start)) {
    issues.push({
      nodeId,
      nodeKind,
      field: 'startDate',
      code: 'DATE_OUT_OF_BOUNDS',
      message: `Start date must be between ${MIN_REASONABLE_YEAR} and ${MAX_REASONABLE_YEAR}.`,
    })
  }

  if (!isValidDate(end)) {
    if (requireDates) {
      issues.push({
        nodeId,
        nodeKind,
        field: 'endDate',
        code: 'DATE_END_MISSING',
        message: 'End date is missing or invalid.',
      })
    }
  } else if (dateOutOfBounds(end)) {
    issues.push({
      nodeId,
      nodeKind,
      field: 'endDate',
      code: 'DATE_OUT_OF_BOUNDS',
      message: `End date must be between ${MIN_REASONABLE_YEAR} and ${MAX_REASONABLE_YEAR}.`,
    })
  }

  if (dateRangeInverted(start, end)) {
    issues.push({
      nodeId,
      nodeKind,
      field: 'endDate',
      code: 'DATE_RANGE_INVERTED',
      message: 'End date is before the start date.',
    })
  }
}

const validateChecklist = (
  items: ChecklistItem[],
  parentNodeId: string,
  parentNodeKind: ValidationIssue['nodeKind'],
  issues: ValidationIssue[],
): void => {
  for (const item of items) {
    if (item.text.trim().length === 0) {
      issues.push({
        nodeId: item.id,
        nodeKind: 'checklist',
        field: 'text',
        code: 'CHECKLIST_TEXT_EMPTY',
        message: 'Checklist item title is empty.',
      })
    }
  }
  // parent reference kept for future cross-checks; explicitly unused for now
  void parentNodeId
  void parentNodeKind
}

/**
 * Validate a single Goal/Task/Subtask without recursion into descendants. Used
 * by the Manage screen save (which validates one node at a time) and by the
 * review screen's per-node re-check on edits.
 */
export const validateNode = (
  node: Goal | Task | SubTask,
  nodeKind: 'goal' | 'task' | 'subtask',
  parent?: { startDate: TimelineDate; endDate: TimelineDate },
  options?: { requireDates?: boolean },
): ValidationIssue[] => {
  const issues: ValidationIssue[] = []
  const requireDates = options?.requireDates ?? true
  validateTitle(node.title, node.id, nodeKind, issues)
  validateDates(node.startDate, node.endDate, node.id, nodeKind, issues, requireDates)
  if (
    parent &&
    requireDates &&
    isValidDate(node.startDate) &&
    isValidDate(node.endDate) &&
    !dateInsideParent(node.startDate, node.endDate, parent.startDate, parent.endDate)
  ) {
    issues.push({
      nodeId: node.id,
      nodeKind,
      field: 'startDate',
      code: 'DATE_OUTSIDE_PARENT',
      message: 'Dates fall outside the parent\'s range.',
    })
  }
  validateChecklist(node.checklist, node.id, nodeKind, issues)
  return issues
}

/**
 * Walk the full goal tree and return every validation issue. Used by the
 * review screen to drive the yellow ⚠️ chips + global Save lock.
 */
export const validateGoalTree = (goal: Goal, options?: { requireDates?: boolean }): ValidationIssue[] => {
  const issues: ValidationIssue[] = []
  issues.push(...validateNode(goal, 'goal', undefined, options))
  for (const task of goal.tasks) {
    issues.push(
      ...validateNode(task, 'task', { startDate: goal.startDate, endDate: goal.endDate }, options),
    )
    for (const subTask of task.subTasks) {
      issues.push(
        ...validateNode(
          subTask,
          'subtask',
          { startDate: task.startDate, endDate: task.endDate },
          options,
        ),
      )
    }
  }
  return issues
}

/** Group issues by node id so the tree can render a single chip per row. */
export const groupIssuesByNode = (issues: ValidationIssue[]): Map<string, ValidationIssue[]> => {
  const map = new Map<string, ValidationIssue[]>()
  for (const issue of issues) {
    const list = map.get(issue.nodeId) ?? []
    list.push(issue)
    map.set(issue.nodeId, list)
  }
  return map
}

/** Convenience: true if the tree has zero validation issues. */
export const isGoalTreeValid = (goal: Goal): boolean => validateGoalTree(goal).length === 0
